import { ReactElement, useId } from 'react';
import {
  getBarRenderFloor,
  getIndirectHeight,
  getSegmentLayout,
  getSolidHeight,
  ScenarioPlanningChartEntry,
  shouldShowFullBarBorder
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { Brand100 } from '../../utils/colors';

const ACTIVE_BORDER_WIDTH = 2;
const BORDER_RADIUS = 4;
export const MIN_BAR_HEIGHT = BORDER_RADIUS;

const roundedTopPath = (px: number, py: number, pw: number, ph: number, r: number): string =>
  [
    `M ${px},${py + ph}`,
    `L ${px},${py + r}`,
    `Q ${px},${py} ${px + r},${py}`,
    `L ${px + pw - r},${py}`,
    `Q ${px + pw},${py} ${px + pw},${py + r}`,
    `L ${px + pw},${py + ph}`,
    'Z'
  ].join(' ');

const roundedRectPath = (px: number, py: number, pw: number, ph: number, r: number): string =>
  [
    `M ${px + r},${py}`,
    `L ${px + pw - r},${py}`,
    `Q ${px + pw},${py} ${px + pw},${py + r}`,
    `L ${px + pw},${py + ph - r}`,
    `Q ${px + pw},${py + ph} ${px + pw - r},${py + ph}`,
    `L ${px + r},${py + ph}`,
    `Q ${px},${py + ph} ${px},${py + ph - r}`,
    `L ${px},${py + r}`,
    `Q ${px},${py} ${px + r},${py}`,
    'Z'
  ].join(' ');

// Direct bars and the full-bar border share the same anchored-vs-floating
// rule: top is always rounded, bottom is rounded only when the bar floats
// off the x-axis (waterFallValue > 0). Indirect bars and the indirect-only
// border keep top-rounded / square-bottom regardless because their bottom
// edge always meets the direct bar above.
const barPath = (
  px: number,
  py: number,
  pw: number,
  ph: number,
  r: number,
  isFloating: boolean
): string => (isFloating ? roundedRectPath(px, py, pw, ph, r) : roundedTopPath(px, py, pw, ph, r));

export interface ShapeProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  payload: ScenarioPlanningChartEntry;
}

export const ScenarioPlanningDirectBarShape = ({
  x,
  y,
  width,
  height,
  fill,
  payload
}: ShapeProps): ReactElement => {
  // Unique per rendered bar; useId's colons are not valid inside a url(#...) reference.
  const clipId = `segclip-${useId().replace(/:/g, '')}`;
  if (
    !width ||
    width <= 0 ||
    !height ||
    height <= 0 ||
    Math.abs(getSolidHeight(payload)) < getBarRenderFloor(payload.metric)
  ) {
    return <g />;
  }
  const renderHeight = Math.max(height, MIN_BAR_HEIGHT);
  const renderY = y + height - renderHeight;
  const d = barPath(x, renderY, width, renderHeight, BORDER_RADIUS, payload.waterFallValue > 0);
  const showBorder = payload.showActualized && !payload.showBreakout;
  const segmentLayout = getSegmentLayout(payload);
  if (segmentLayout) {
    // Per-model segments: stacked shades of the channel color, first model at the bottom,
    // clipped to the same rounded bar path so corners, border and blending are unchanged.
    const rects = segmentLayout.fractions.map((fraction, index) => {
      const h = fraction * renderHeight;
      const below =
        segmentLayout.fractions.slice(0, index).reduce((sum, f) => sum + f, 0) * renderHeight;
      return (
        <rect
          key={index}
          x={x}
          y={renderY + renderHeight - below - h}
          width={width}
          height={h}
          fill={segmentLayout.fills[index]}
        />
      );
    });
    return (
      <g style={{ mixBlendMode: 'multiply' }}>
        <clipPath id={clipId}>
          <path d={d} />
        </clipPath>
        <g clipPath={`url(#${clipId})`}>{rects}</g>
        {showBorder && (
          <path d={d} fill='none' stroke={Brand100} strokeWidth={ACTIVE_BORDER_WIDTH} />
        )}
      </g>
    );
  }
  if (showBorder) {
    return (
      <g style={{ mixBlendMode: 'multiply' }}>
        <path d={d} fill={fill} />
        <path d={d} fill='none' stroke={Brand100} strokeWidth={ACTIVE_BORDER_WIDTH} />
      </g>
    );
  }
  return <path d={d} fill={fill} style={{ mixBlendMode: 'multiply' }} />;
};

export const ScenarioPlanningIndirectBarShape = ({
  x,
  y,
  width,
  height,
  fill,
  payload
}: ShapeProps): ReactElement => {
  if (
    !width ||
    width <= 0 ||
    !height ||
    height <= 0 ||
    Math.abs(getIndirectHeight(payload)) < getBarRenderFloor(payload.metric)
  ) {
    return <g />;
  }
  const fillRect = (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      style={{ mixBlendMode: 'multiply' }}
    />
  );

  const indirectH = getIndirectHeight(payload);
  const solidH = getSolidHeight(payload);
  if (!indirectH || !solidH) {
    return fillRect;
  }
  const solidPx = (solidH / indirectH) * height;

  const borderPath = shouldShowFullBarBorder(payload)
    ? barPath(x, y, width, height + solidPx, BORDER_RADIUS, payload.waterFallValue > 0)
    : roundedTopPath(x, y + height, width, solidPx, BORDER_RADIUS);

  return (
    <g style={{ mixBlendMode: 'multiply' }}>
      {fillRect}
      <path d={borderPath} fill='none' stroke={Brand100} strokeWidth={ACTIVE_BORDER_WIDTH} />
    </g>
  );
};
